'use client';

import React, { useState } from 'react';
import { MOCK_MOVEMENTS } from './inventoryData';
import { ArrowDownCircle, ArrowUpCircle, RefreshCw } from 'lucide-react';

const TYPE_COLORS: Record<string, { bg: string; color: string }> = {
  GRN:        { bg: '#f0fdf4', color: '#15803d' },
  GIN:        { bg: '#fef2f2', color: '#dc2626' },
  Transfer:   { bg: '#eff6ff', color: '#2563eb' },
  Adjustment: { bg: '#fffbeb', color: '#d97706' },
  Return:     { bg: '#faf5ff', color: '#7c3aed' },
  Opening:    { bg: '#f1f5f9', color: '#64748b' },
};

export default function StockMovementsPanel() {
  const [filterType, setFilterType] = useState('all');

  const filtered = filterType === 'all' ? MOCK_MOVEMENTS : MOCK_MOVEMENTS.filter((m) => m.type === filterType);

  return (
    <div className="panel-card">
      <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-b" style={{ borderColor: 'var(--border)' }}>
        <div>
          <h3 className="text-sm font-semibold text-foreground">Stock Movements</h3>
          <p className="text-xs text-muted-foreground">All GRN, GIN, transfers and adjustments</p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="select-field text-sm"
            style={{ height: '32px', width: '140px' }}
          >
            {['all', 'GRN', 'GIN', 'Transfer', 'Adjustment', 'Return'].map((t) => (
              <option key={`mt-${t}`} value={t}>{t === 'all' ? 'All Types' : t}</option>
            ))}
          </select>
        </div>
      </div>
      <div className="overflow-x-auto scrollbar-thin">
        <table className="w-full" style={{ minWidth: '900px' }}>
          <thead>
            <tr style={{ backgroundColor: 'var(--muted)', borderBottom: '1px solid var(--border)' }}>
              {['Date', 'Reference', 'Type', 'Item', 'Store', 'Direction', 'Qty', 'Unit Cost', 'Total Value', 'Balance After', 'Raised By'].map((h) => (
                <th key={`smh-${h}`} className="px-3 py-2.5 text-left text-xs font-semibold text-muted-foreground" style={{ letterSpacing: '0.02em' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.map((mov) => {
              const typeStyle = TYPE_COLORS[mov.type] ?? { bg: '#f1f5f9', color: '#64748b' };
              return (
                <tr key={mov.id} className="table-row-hover border-b transition-colors" style={{ borderColor: 'var(--border)' }}>
                  <td className="px-3 py-2.5 text-xs font-tabular text-foreground">{mov.date}</td>
                  <td className="px-3 py-2.5">
                    <span className="text-xs font-mono font-semibold" style={{ color: 'var(--primary)' }}>{mov.reference}</span>
                  </td>
                  <td className="px-3 py-2.5">
                    <span
                      className="text-xs font-semibold px-2 py-0.5 rounded"
                      style={{ backgroundColor: typeStyle.bg, color: typeStyle.color }}
                    >
                      {mov.type}
                    </span>
                  </td>
                  <td className="px-3 py-2.5">
                    <p className="text-xs font-medium text-foreground">{mov.itemName}</p>
                    <p className="text-xs text-muted-foreground">{mov.itemCode}</p>
                  </td>
                  <td className="px-3 py-2.5 text-xs text-foreground">{mov.store}</td>
                  <td className="px-3 py-2.5">
                    <div className="flex items-center gap-1.5">
                      {mov.direction === 'in'
                        ? <ArrowUpCircle size={13} style={{ color: 'var(--success)' }} />
                        : <ArrowDownCircle size={13} style={{ color: 'var(--danger)' }} />
                      }
                      <span
                        className="text-xs font-semibold"
                        style={{ color: mov.direction === 'in' ? 'var(--success)' : 'var(--danger)' }}
                      >
                        {mov.direction === 'in' ? 'IN' : 'OUT'}
                      </span>
                    </div>
                  </td>
                  <td className="px-3 py-2.5">
                    <span
                      className="text-sm font-bold font-tabular"
                      style={{ color: mov.direction === 'in' ? 'var(--success)' : 'var(--danger)' }}
                    >
                      {mov.direction === 'in' ? '+' : '-'}{mov.qty.toLocaleString()}
                    </span>
                  </td>
                  <td className="px-3 py-2.5 text-xs font-tabular text-foreground">
                    UGX {mov.unitCost.toLocaleString()}
                  </td>
                  <td className="px-3 py-2.5 text-xs font-tabular font-semibold text-foreground">
                    UGX {(mov.totalValue / 1000).toFixed(0)}K
                  </td>
                  <td className="px-3 py-2.5 text-xs font-tabular text-foreground">
                    {mov.balanceAfter.toLocaleString()}
                  </td>
                  <td className="px-3 py-2.5 text-xs text-muted-foreground">{mov.raisedBy}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {filtered.length === 0 && (
        <div className="py-12 text-center">
          <RefreshCw size={24} className="mx-auto text-muted-foreground mb-3" />
          <p className="text-sm font-medium text-foreground">No movements found</p>
          <p className="text-xs text-muted-foreground mt-1">No stock movements match the selected filter.</p>
        </div>
      )}

      <div className="px-4 py-3 border-t flex items-center justify-between" style={{ borderColor: 'var(--border)' }}>
        <p className="text-xs text-muted-foreground">
          Showing {filtered.length} of {MOCK_MOVEMENTS.length} movements
        </p>
        <p className="text-xs text-muted-foreground">
          Net movement value:{' '}
          <strong className="font-tabular text-foreground">
            UGX {filtered.reduce((s, m) => s + (m.direction === 'in' ? m.totalValue : -m.totalValue), 0).toLocaleString()}
          </strong>
        </p>
      </div>
    </div>
  );
}